import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApiGet, useApiPost, useApiPut } from 'hooks/useApi';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import CustomerCompanies, { Addresses } from 'models/CustomerCompanies';
import useUser from 'hooks/useUser';
import CustomerUser from 'models/CustomerUser';
import Pagination from 'models/Pagination';
import NavCompaniesFormPage from '../enum/navCompaniesFormPage';
import apiPaths from 'constants/apiPaths';

const yupSchema = yup
  .object({
    name: yup.string().required('O nome fantasia é obrigatório.'),
    email: yup.string().email().required(),
    razaoSocial: yup.string().required('A razão social é obrigatória.'),
    addresses: yup
      .array()
      .of(
        yup.object().shape({
          street: yup.string().required("O campo 'Rua' é obrigatório."),
          number: yup.string().required("O campo 'Número' é obrigatório."),
          complement: yup.string(),
          district: yup.string().required("O campo 'Bairro' é obrigatório."),
          city: yup.string().required("O campo 'Cidade' é obrigatório."),
          state: yup.string().required("O campo 'Estado' é obrigatório."),
          zipCode: yup.string().required("O campo 'CEP' é obrigatório."),
          mainAddress: yup.string().oneOf(['true', 'false']).default('false'),
          billingAddress: yup
            .string()
            .oneOf(['true', 'false'])
            .default('false')
            .when('mainAddress', {
              is: 'true',
              then: (schema) => schema.default('true')
            })
        })
      )
      .test(
        'addresses',
        'Endereço principal e de cobrança devem ser informados',
        (value) => {
          return (
            value?.length === 2 ||
            (value?.length === 1 && value[0].billingAddress === 'true')
          );
        }
      ),
    users: yup.array(),
    phone: yup.string(),
    cellPhone: yup.string(),
    cnpj: yup.string().required('O CNPJ é obrigatório.'),
    stateRegistration: yup.string(),
    municipalRegistration: yup.string()
  })
  .required();

const useControllerCustomerCompaniesFormPage = () => {
  const params = useParams();
  const user = useUser();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<string>(
    NavCompaniesFormPage.GENRAL_DATA
  );
  const [isNext, setIsNext] = useState(true);
  const { data, isLoading: isFetching } = useApiGet<CustomerCompanies>(
    apiPaths.customerCompanies,
    { id: params.id, enabled: !!params.id }
  );
  const { mutate: mutateCreate, isPending: isCreating } = useApiPost<
    CustomerCompanies
  >(apiPaths.customerCompanies);
  const { mutate: mutateUpdate, isPending: isUpdating } = useApiPut<
    CustomerCompanies
  >(apiPaths.customerCompanies, params.id);

  const {
    data: customerUsersData,
    refetch: customerUsersRefetch,
    isLoading: isLoadingCustomerUsers
  } = useApiGet<Pagination<CustomerUser>>(apiPaths.customerUsers);

  const mutate = params.id ? mutateUpdate : mutateCreate;

  const userCanPerformAction = useMemo(() => {
    if (params.id) {
      return user?.resources?.[TypeResource.COMPANIES]?.includes(
        TypeAction.UPDATE
      );
    } else {
      return user?.resources?.[TypeResource.COMPANIES]?.includes(
        TypeAction.CREATE
      );
    }
  }, [user, params.id]);

  const isSaving = isCreating || isUpdating;

  const customerCompanies = params.id ? data : undefined;

  const formMethods = useForm({
    resolver: yupResolver(yupSchema)
  });

  const billingAddress = useWatch({
    control: formMethods.control,
    name: 'addresses.0.billingAddress',
    defaultValue: 'false'
  });

  const watchedFields = useWatch({
    control: formMethods.control
  });

  useEffect(() => {
    if (billingAddress === 'true') {
      formMethods.clearErrors('addresses.1');
      formMethods.unregister('addresses.1');
    }
  }, [billingAddress, formMethods]);

  const canProceedToAddress = useMemo(() => {
    const { name, razaoSocial, cnpj, users } = watchedFields;
    return !!name && !!razaoSocial && !!cnpj && users?.length;
  }, [watchedFields]);

  const canSubmit = useMemo(() => {
    const { email, phone, addresses = [] } = watchedFields;

    const addressValid =
      addresses[0]?.street && addresses[0]?.city && addresses[0]?.state;

    return !!email && !!phone && addressValid;
  }, [watchedFields]);

  const handleNext = () => {
    if (activeTab === NavCompaniesFormPage.GENRAL_DATA && canProceedToAddress) {
      setActiveTab(NavCompaniesFormPage.ADDRESS);
      setIsNext(false);
    }
  };

  const handlePrevious = () => {
    if (activeTab === NavCompaniesFormPage.ADDRESS) {
      setActiveTab(NavCompaniesFormPage.GENRAL_DATA);
      setIsNext(true);
    }
  };

  const updateActiveTab = (tab: string) => {
    setActiveTab(tab);
    setIsNext(tab === NavCompaniesFormPage.GENRAL_DATA);
  };

  const onError = useFieldSetErrors(formMethods.setError);

  const handleOnSubmit = useCallback(
    (data: any) => {
      if (!userCanPerformAction) return;
      if (!isNext && canSubmit) {
        const dataUsers = data?.users?.map((user: any) => user.value);
        const dataAddresses: Addresses[] = [
          data?.addresses[0],
          data?.addresses[0]?.billingAddress === 'false'
            ? data?.addresses[1]
            : null
        ]
          .filter(Boolean)
          ?.map((address: Addresses, index: number) => ({
            ...address,
            mainAddress: index === 0,
            billingAddress:
              index === 1 || data?.addresses[0].billingAddress === 'true'
          }));
        mutate(
          { ...data, users: dataUsers, addresses: dataAddresses },
          {
            onError,
            onSuccess: () => {
              addToast('success', 'Empresa salva com sucesso');
              navigate(apiPaths.customerCompanies);
            }
          }
        );
      }
    },
    [
      addToast,
      mutate,
      navigate,
      onError,
      userCanPerformAction,
      isNext,
      canSubmit
    ]
  );

  const customerUsersOptions = useMemo(() => {
    const data = customerUsersData?.items || [];
    return data.map((user) => ({
      label: user.firstName + ' ' + user.lastName,
      value: user.id
    }));
  }, [customerUsersData]);

  useEffect(() => {
    if (customerCompanies) {
      formMethods.reset({
        name: customerCompanies.name,
        email: customerCompanies.email,
        razaoSocial: customerCompanies.razaoSocial,
        phone: customerCompanies.phone,
        cellPhone: customerCompanies.cellPhone,
        cnpj: customerCompanies.cnpj,
        stateRegistration: customerCompanies.stateRegistration,
        municipalRegistration: customerCompanies.municipalRegistration,
        users: customerCompanies.users?.map((user) => ({
          label: user.firstName + ' ' + user.lastName,
          value: user.id
        })),
        addresses: [
          customerCompanies.addresses?.find((address) => address.mainAddress),
          customerCompanies.addresses?.find(
            (address) => address.billingAddress && !address.mainAddress
          )
        ]
          .filter(Boolean)
          .map((address) => ({
            ...address,
            mainAddress: address?.mainAddress || 'false',
            billingAddress: address?.billingAddress || 'false'
          }))
      } as any);
    }
  }, [customerCompanies, formMethods]);

  return {
    formMethods,
    handleOnSubmit,
    customerUsersOptions,
    isFetching,
    isSaving,
    customerCompanies,
    userCanPerformAction,
    isLoadingCustomerUsers,
    customerUsersRefetch,
    params,
    billingAddress,
    canProceedToAddress,
    activeTab,
    setActiveTab,
    isNext,
    setIsNext,
    canSubmit,
    handleNext,
    handlePrevious,
    updateActiveTab
  };
};

export default useControllerCustomerCompaniesFormPage;
